import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxCard, CheckboxCardSkeleton } from "@/components/CheckboxCard";
import { FormActions } from "@jbnl/shared-ui";
import { SelectAllCheckbox } from "@/components/SelectAllCheckbox";
import { Button } from "@/components/ui/button";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { datumLang } from "@/i18n/datum";
import {
  useBeschikbaarheid,
  useSetBeschikbaarheid,
} from "./useBeschikbaarheid";

interface Props {
  inschrijfperiodeId: string | undefined;
  persoonId: string | undefined;
}

type Antwoord = NonNullable<ReturnType<typeof useBeschikbaarheid>["data"]>;

type Verander = (volgende: (huidig: string[]) => string[]) => void;

const toggle = (id: string) => (huidig: string[]) =>
  huidig.includes(id) ? huidig.filter((x) => x !== id) : [...huidig, id];

/** The ids already ticked in the server's answer. */
const gekozenUit = (data: Antwoord | undefined) => ({
  dagen:
    data?.dagen.filter((d) => d.beschikbaar).map((d) => d.wedstrijddagId) ?? [],
  klassen: data?.klassen.filter((k) => k.gewenst).map((k) => k.klasseId) ?? [],
});

/**
 * Saved as a whole set: geen beschikbaarheid is the absence of a row, so an unticked box is the
 * only way to say "not this one". Days and an optional klasse wish, never a functie - the
 * planning draws that from the bevoegdheden (ADR-0077).
 */
export function BeschikbaarheidForm({ inschrijfperiodeId, persoonId }: Props) {
  const { t } = useTranslation(["beschikbaarheid", "common"]);
  const { data, isPending, isError } = useBeschikbaarheid(
    inschrijfperiodeId,
    persoonId,
  );
  const opslaan = useSetBeschikbaarheid(inschrijfperiodeId, persoonId);

  const [gekozenDagen, setGekozenDagen] = useState<string[]>([]);
  const [gekozenKlassen, setGekozenKlassen] = useState<string[]>([]);
  const [geladen, setGeladen] = useState(data);

  // Re-seeded during render whenever the server's answer changes - including after a save, and
  // after the beheerder picks a different persoon. Not an effect: an effect would render the
  // stale ticks once before correcting them.
  if (data !== geladen) {
    const uit = gekozenUit(data);
    setGeladen(data);
    setGekozenDagen(uit.dagen);
    setGekozenKlassen(uit.klassen);
  }

  if (!inschrijfperiodeId || !persoonId) {
    return null;
  }

  if (isPending) {
    return <BeschikbaarheidFormSkeleton />;
  }

  if (isError) {
    return <p className="text-sm text-destructive">{t("errors.ophalen")}</p>;
  }

  if (data.dagen.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("geenDagen")}</p>;
  }

  const actions = <Acties opslaan={opslaan} />;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();

        opslaan.mutate({
          wedstrijddagIds: gekozenDagen,
          klasseIds: gekozenKlassen,
        });
      }}
    >
      {actions}

      <DagenKeuze
        dagen={data.dagen}
        gekozen={gekozenDagen}
        onChange={setGekozenDagen}
      />

      {data.klassen.length > 0 && (
        <KlassenKeuze
          klassen={data.klassen}
          gekozen={gekozenKlassen}
          onChange={setGekozenKlassen}
        />
      )}

      {actions}
    </form>
  );
}

/** Drawn at both ends since #117: the outcome travels with the button that was pressed. */
function Acties({
  opslaan,
}: {
  opslaan: ReturnType<typeof useSetBeschikbaarheid>;
}) {
  const { t } = useTranslation(["beschikbaarheid", "common"]);

  return (
    <FormActions>
      {opslaan.isSuccess && !opslaan.isPending && (
        <p className="text-sm text-muted-foreground">{t("opgeslagen")}</p>
      )}

      {opslaan.isError && (
        <p className="text-sm text-destructive">{opslaan.error.message}</p>
      )}

      <Button type="submit" disabled={opslaan.isPending}>
        {opslaan.isPending ? t("common:actions.busy") : t("opslaan")}
      </Button>
    </FormActions>
  );
}

function DagenKeuze({
  dagen,
  gekozen,
  onChange,
}: {
  dagen: Antwoord["dagen"];
  gekozen: string[];
  onChange: Verander;
}) {
  const { t } = useTranslation("beschikbaarheid");

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("legend")}</FieldLegend>

      <SelectAllCheckbox
        id="beschikbaarheid-alles"
        total={dagen.length}
        selected={gekozen.length}
        onChange={(alles) =>
          onChange(() => (alles ? dagen.map((dag) => dag.wedstrijddagId) : []))
        }
      />

      <ul className="grid gap-2 sm:grid-cols-2">
        {dagen.map((dag) => (
          <li key={dag.wedstrijddagId}>
            <CheckboxCard
              id={`dag-${dag.wedstrijddagId}`}
              title={datumLang(dag.date)}
              description={t("dagRegel", {
                niveau: dag.niveauName,
                korps: dag.organiserendKorpsName,
              })}
              checked={gekozen.includes(dag.wedstrijddagId)}
              onCheckedChange={() => onChange(toggle(dag.wedstrijddagId))}
            />
          </li>
        ))}
      </ul>
    </FieldSet>
  );
}

function KlassenKeuze({
  klassen,
  gekozen,
  onChange,
}: {
  klassen: Antwoord["klassen"];
  gekozen: string[];
  onChange: Verander;
}) {
  const { t } = useTranslation("beschikbaarheid");

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("klassenLegend")}</FieldLegend>

      <ul className="grid gap-2 sm:grid-cols-2">
        {klassen.map((klasse) => (
          <li key={klasse.klasseId}>
            <CheckboxCard
              id={`klasse-${klasse.klasseId}`}
              title={klasse.klasseName}
              checked={gekozen.includes(klasse.klasseId)}
              onCheckedChange={() => onChange(toggle(klasse.klasseId))}
            />
          </li>
        ))}
      </ul>
    </FieldSet>
  );
}

/** Both rows, or the list drops down the page when the answer lands. */
function BeschikbaarheidFormSkeleton() {
  const { t } = useTranslation(["beschikbaarheid", "common"]);

  const skeletonActions = (
    <FormActions>
      <Skeleton className="h-9 w-48" />
    </FormActions>
  );

  return (
    <SkeletonRegion className="space-y-4">
      {skeletonActions}

      <FieldSet>
        <FieldLegend variant="label">{t("legend")}</FieldLegend>

        {/* The tri-state box above the list, at the height its own row will have. */}
        <div className="flex h-5 w-auto items-center gap-2">
          <Skeleton className="size-4 rounded-sm" />
          <Skeleton className="h-3.5 w-44" />
        </div>

        <ul className="grid gap-2 sm:grid-cols-2">
          {Array.from({ length: 6 }, (_, dag) => (
            <li key={dag}>
              <CheckboxCardSkeleton />
            </li>
          ))}
        </ul>
      </FieldSet>

      {skeletonActions}
    </SkeletonRegion>
  );
}
